import { useCallback, useEffect, useState } from 'react';
import { nextScrollHideState, type ScrollHideState } from '@shared/lib/scrollHide';

/**
 * Hide-on-scroll-down / reveal-on-scroll-up for a scroll container.
 *
 * Returns `hidden` (true while scrolling down, false while scrolling up or resting
 * near the top) plus `containerRef` to attach to the scroll element. It uses the
 * same thresholds as the top header (see scrollHide.ts) and reads the same inner
 * scroll container rather than the window, so the two stay in sync.
 *
 * `containerRef` is backed by a state setter, so a keyed scroller that remounts
 * (the shell re-mounts <main> per route) re-subscribes on its own and resets to
 * shown, since a fresh page is scrolled to the top.
 *
 * Pass `enabled: false` to stop tracking and force shown (e.g. on routes where the
 * bar is removed entirely), so this composes with rather than fights that hiding.
 */
export function useHideOnScroll(enabled = true): {
  hidden: boolean;
  containerRef: (node: HTMLElement | null) => void;
} {
  const [hidden, setHidden] = useState(false);
  const [node, setNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Any (re)subscribe starts from shown: a fresh page sits at the top.
    setHidden(false);
    if (!node || !enabled) return;

    let state: ScrollHideState = { hidden: false, last: node.scrollTop };
    let frame = 0;

    // rAF-throttle: fold a burst of scroll events into one read per frame.
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const next = nextScrollHideState(node.scrollTop, state);
        if (next.hidden !== state.hidden) setHidden(next.hidden);
        state = next;
      });
    };

    node.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      node.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [node, enabled]);

  const containerRef = useCallback((n: HTMLElement | null) => setNode(n), []);
  return { hidden, containerRef };
}
